import React, { useState, useEffect, useRef } from "react";
import { Hooks, Util, Request, Context } from "@xt/xtbase";
const { useTranslation } = Hooks;
const { getUrl, Big, showRate } = Util;
const { XtSocketPublic } = Request;
const { XtContext } = Context;
import { get_ticker24h } from "@/api/v4/market";
import { post_symbolStar, delete_symbolStarId } from "@/api/v4/account";
import { get_hotSearchMarket /* , get_newestMarket */ } from "@/api/xt-app";
import { useStore } from "@/models";
import { observer } from "mobx-react-lite";
import { message } from "antd";
import HomeCampaignBanner from "@/components/Pages/Home/HomeAssetOverview/HomeCampaignBanner";
import { isFiatMarketCode } from "@/utils/fiatMarket";
import styles from "./index.module.less";

/** 与 currencyList / Market 逻辑一致：plates 含 258 视为法币币种 */
const GLOBAL_FIAT_PLATE_ID = 258;

/** 解析 CFD 币对计价币种，如 eth_brl_cfd → BRL */
function getCfdQuoteCurrency(symbol) {
  if (!symbol) return "";
  const parts = symbol.split("_");
  if (parts.length < 2) return "";
  return String(parts[1]).toUpperCase();
}

function isQuoteFiatByCurrencyPlates(quote, currencyMap, currencyList) {
  if (!quote) return false;
  const upper = String(quote).toUpperCase();
  const c =
    (currencyMap &&
      (currencyMap[upper] ||
        currencyMap[String(quote).toLowerCase()] ||
        currencyMap[quote])) ||
    null;
  if (c) {
    const plates = Array.isArray(c.plates) ? c.plates : [];
    if (plates.length)
      return plates.some((p) => Number(p) === GLOBAL_FIAT_PLATE_ID);
  }
  if (Array.isArray(currencyList)) {
    const item = currencyList.find(
      (x) => String(x.currency || x.coinName || "").toUpperCase() === upper
    );
    if (item) {
      const plates = Array.isArray(item.plates) ? item.plates : [];
      return plates.some((p) => Number(p) === GLOBAL_FIAT_PLATE_ID);
    }
  }
  return false;
}

/**
 * 与 MarketList fiat-m / usdt-m 并集一致：CFD 法币盘仅展示 recommendedCurrencyList 命中的法币；
 * U 本位等非全局法币计价仍展示。
 */
function passesSearchCfdFiatFilter(symbol, marketEntry, currencyMap, currencyList) {
  if (!marketEntry) return false;
  const normalizedType = String(marketEntry.type || "").toUpperCase();
  if (normalizedType !== "CFD") return true;
  const quote = getCfdQuoteCurrency(symbol);
  if (!quote) return true;
  if (quote === "USDT") return true;
  if (isFiatMarketCode(quote)) return true;
  if (isQuoteFiatByCurrencyPlates(quote, currencyMap, currencyList)) return false;
  return true;
}

const _starY = require("@/assets/images/search/star-filled-yellow.svg");
const _starG = require("@/assets/images/search/star-filled-gray.svg");
const _del = require("@/assets/images/search/delete-f.svg");
const _search = require("@/assets/images/search/search-f.svg");
const _arrowL = require("@/assets/images/search/arrow-left-f.svg");
const _closeC = require("@/assets/images/search/circled-close-f.svg");
const _notFound = require("@/assets/images/search/not-found-data.svg");
const starFilledYellow = _starY && (_starY.default || _starY);
const starFilledGray = _starG && (_starG.default || _starG);
const deleteIconSrc = _del && (_del.default || _del);
const searchIconSrc = _search && (_search.default || _search);
const arrowLeftSrc = _arrowL && (_arrowL.default || _arrowL);
const circledCloseSrc = _closeC && (_closeC.default || _closeC);
const notFoundDataSrc = _notFound && (_notFound.default || _notFound);

const HomeSearchOverlay = ({ visible, onClose }) => {
  const t = useTranslation();
  const { fund, market, user } = useStore();
  const [appState] = React.useContext(XtContext);
  const loginStatus = appState?.loginStatus;
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [topSearches, setTopSearches] = useState([]);
  // const [topNewTokens, setTopNewTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  // const [activeTab, setActiveTab] = useState("searches"); // 仅保留 Top Search，与 Top New Tokens 相关逻辑已注释
  const [favoriteLoading, setFavoriteLoading] = useState({});
  const [searchHistory, setSearchHistory] = useState([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const inputRef = useRef(null);

  // localStorage key
  const SEARCH_HISTORY_KEY = "home_search_history";
  const MAX_HISTORY_COUNT = 20;

  // 判断是否为CFD交易对（带_cfd后缀的为CFD币对）
  const isCfdSymbol = (symbol) => {
    if (!symbol) return false;
    return symbol.toLowerCase().includes("_cfd");
  };

  // 获取币种名称
  const getCoinName = (symbol) => {
    if (!symbol) return "";
    return symbol.split("_")[0];
  };

  // 获取市场名称
  const getMarketName = (symbol) => {
    if (!symbol) return "";
    return symbol.split("_").length > 1 ? symbol.split("_")[1] : "";
  };

  // 从localStorage加载搜索历史
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem(SEARCH_HISTORY_KEY);
        if (saved) {
          const history = JSON.parse(saved);
          setSearchHistory(Array.isArray(history) ? history : []);
        }
      } catch (error) {
        console.error("Failed to load search history:", error);
      }
    }
  }, []);

  // 超过7个就显示展开/收起按钮
  const MAX_ITEMS_BEFORE_EXPAND = 7;
  const showExpandButton = searchHistory.length > MAX_ITEMS_BEFORE_EXPAND;

  // 保存搜索历史到localStorage
  const saveSearchHistory = (history) => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(history));
        setSearchHistory(history);
      } catch (error) {
        console.error("Failed to save search history:", error);
      }
    }
  };

  // 添加搜索历史
  const addToSearchHistory = (symbol) => {
    if (!symbol) return;
    
    const displayName = `${getCoinName(symbol).toUpperCase()}/${getMarketName(symbol).toUpperCase()}`;
    const historyItem = { symbol, displayName, timestamp: Date.now() };
    
    let newHistory = [...searchHistory];
    
    // 移除已存在的相同项
    newHistory = newHistory.filter((item) => item.symbol !== symbol);
    
    // 添加到最前面
    newHistory.unshift(historyItem);
    
    // 限制最多20条
    if (newHistory.length > MAX_HISTORY_COUNT) {
      newHistory = newHistory.slice(0, MAX_HISTORY_COUNT);
    }
    
    saveSearchHistory(newHistory);
  };

  // 删除单个搜索历史
  const removeFromSearchHistory = (symbol, e) => {
    e.stopPropagation();
    const newHistory = searchHistory.filter((item) => item.symbol !== symbol);
    saveSearchHistory(newHistory);
  };

  // 清除所有搜索历史
  const clearAllSearchHistory = (e) => {
    e.stopPropagation();
    saveSearchHistory([]);
  };

  // 点击搜索历史项
  const handleHistoryClick = (symbol) => {
    if (symbol) {
      addToSearchHistory(symbol);
      if (isCfdSymbol(symbol)) {
        location.href = getUrl(`/futures/detail/${symbol}`);
      } else {
        // 暂无现货，原现货跳转：
        // location.href = getUrl(`/trade/${symbol}`);
      }
    }
  };

  // 打开层：热门数据 + 聚焦（不随登录态重复拉热门）
  useEffect(() => {
    if (visible) {
      initTopData();
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    } else {
      setSearchValue("");
      setSearchResults([]);
    }
  }, [visible]);

  // 与 XtContext 对齐 store.user.loginStatus 后再拉 symbol-star/list（避免仅 appState 已登录而 store 未同步导致 getUserMarket 直接 return）
  useEffect(() => {
    if (!visible || loginStatus != 1) return;
    user.setLoginStatus(loginStatus);
    user.getUserMarket();
  }, [visible, loginStatus, user]);

  // 初始化热门数据
  const initTopData = async () => {
    try {
      setLoading(true);

      const hotResponse = await get_hotSearchMarket();
      // Top New Tokens 暂不请求：get_newestMarket 已注释
      // const newestResponse = await get_newestMarket();

      // 获取所有市场数据以获取价格信息
      const allMarketData = await get_ticker24h();
      const marketDataMap = {};
      allMarketData.forEach((item) => {
        marketDataMap[item.s] = item;
      });

      // 处理热门搜索数据
      // hot接口直接返回数组: [{ marketName, type }]
      // type: 1=现货, 8/10/11=差价合约
      if (hotResponse && Array.isArray(hotResponse) && hotResponse.length > 0) {
        const hotList = hotResponse.map((item) => {
          const marketName = item.marketName || item.symbol || item;
          const type = item.type;
          // 查找对应的市场数据（可能没有价格数据，但也要显示）
          const marketData = marketDataMap[marketName] || {};
          // 保存 type 信息用于跳转判断
          return {
            ...marketData,
            s: marketName,
            marketType: type,
            // 确保有基本字段
            c: marketData.c || 0,
            r: marketData.r || 0,
            cr: marketData.cr || 0
          };
        });
        setTopSearches(hotList);
      } else {
        console.warn("Hot search data format error or empty:", hotResponse);
        setTopSearches([]);
      }

      // 处理最新币种数据（Top New Tokens 已下线，接口与状态暂注释）
      // if (newestResponse && newestResponse.spots && Array.isArray(newestResponse.spots) && newestResponse.spots.length > 0) {
      //   const newList = newestResponse.spots.map((item) => {
      //     const marketName = item.marketName || item.symbol || item;
      //     const marketData = marketDataMap[marketName] || {};
      //     return {
      //       ...marketData,
      //       s: marketName,
      //       c: marketData.c || 0,
      //       r: marketData.r || 0,
      //       cr: marketData.cr || 0
      //     };
      //   });
      //   setTopNewTokens(newList);
      // } else {
      //   setTopNewTokens([]);
      // }
    } catch (error) {
      console.error("Failed to load top data:", error);
      setTopSearches([]);
      // setTopNewTokens([]);
    } finally {
      setLoading(false);
    }
  };

  // 搜索处理
  const handleSearch = async (value) => {
    setSearchValue(value);
    if (!value || value.trim() === "") {
      setSearchResults([]);
      return;
    }

    const searchTerm = value.trim().toUpperCase();
    const { marketConfig } = market;
    const { currencyMap, currencyList } = fund;

    try {
      // 获取所有市场数据以获取价格信息
      const allMarketData = await get_ticker24h();
      
      // 从市场配置中搜索（CFD 法币盘需与 recommendedCurrencyList 命中法币一致，见 passesSearchCfdFiatFilter）
      const filteredSymbols = Object.keys(marketConfig)
        .filter((symbol) => {
          const config = marketConfig[symbol];
          if (!config || config.state === "DELISTED" || config.displayLevel === "NONE") {
            return false;
          }
          if (!passesSearchCfdFiatFilter(symbol, config, currencyMap, currencyList)) {
            return false;
          }
          const [base, quote] = symbol.split("_");
          return (
            base.toUpperCase().includes(searchTerm) ||
            quote.toUpperCase().includes(searchTerm) ||
            symbol.toUpperCase().includes(searchTerm)
          );
        });

      // 排序：coin匹配优先，market匹配靠后
      const sortedSymbols = filteredSymbols.sort((a, b) => {
        const [aBase, aQuote] = a.split("_");
        const [bBase, bQuote] = b.split("_");
        
        const aCoinMatch = aBase.toUpperCase().includes(searchTerm);
        const bCoinMatch = bBase.toUpperCase().includes(searchTerm);
        const aMarketMatch = aQuote.toUpperCase().includes(searchTerm);
        const bMarketMatch = bQuote.toUpperCase().includes(searchTerm);
        
        // coin匹配优先
        if (aCoinMatch && !bCoinMatch) return -1;
        if (!aCoinMatch && bCoinMatch) return 1;
        
        // 如果都是coin匹配或都不是coin匹配，market匹配的靠后
        if (aCoinMatch && bCoinMatch) {
          // 都是coin匹配，market匹配的靠后
          if (aMarketMatch && !bMarketMatch) return 1;
          if (!aMarketMatch && bMarketMatch) return -1;
        } else {
          // 都不是coin匹配，market匹配的靠后
          if (aMarketMatch && !bMarketMatch) return 1;
          if (!aMarketMatch && bMarketMatch) return -1;
        }
        
        return 0;
      });

      const results = sortedSymbols
        .slice(0, 20)
        .map((symbol) => {
          // 从市场数据中查找对应的价格信息
          const marketData = allMarketData.find((d) => d.s === symbol);
          return marketData || { s: symbol };
        });

      setSearchResults(results);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    }
  };

  // 点击币种
  const handleCoinClick = (symbol, marketType) => {
    if (symbol) {
      addToSearchHistory(symbol);
      if (marketType !== undefined && marketType !== null) {
        if (marketType === 1) {
          // 暂无现货，原现货跳转：
          // location.href = getUrl(`/trade/${symbol}`);
        } else {
          const cfdSymbol = symbol.endsWith("_cfd") ? symbol : `${symbol}_cfd`;
          location.href = getUrl(`/futures/detail/${cfdSymbol}`);
        }
      } else if (isCfdSymbol(symbol)) {
        location.href = getUrl(`/futures/detail/${symbol}`);
      } else {
        // 暂无现货，原现货跳转：
        // location.href = getUrl(`/trade/${symbol}`);
      }
    }
  };

  // 检查是否已收藏
  const isFavorite = (symbol) => {
    if (!symbol || !user.userMarket || !Array.isArray(user.userMarket)) return false;
    // userMarket 中存储的是 symbol 字符串
    return user.userMarket.some((item) => {
      if (typeof item === 'string') {
        return item === symbol;
      }
      // 如果 item 是对象，可能需要检查 item.symbol 或其他字段
      return item === symbol;
    });
  };

  /** 收藏变更后刷新列表：先对齐登录态再请求，与 handleFavorite / 打开层逻辑一致 */
  const refreshFavoriteListAfterMutation = async () => {
    user.setLoginStatus(loginStatus);
    await user.getUserMarket();
  };

  // 处理收藏/取消收藏
  const handleFavorite = async (e, symbol) => {
    e.stopPropagation();
    if (loginStatus != 1) {
      message.warning(t("home.pleaseLogin") || "Please login first");
      // setTimeout(() => {
      //   window.location.href = getUrl("/accounts/login?backurl=" + encodeURIComponent(window.location.href));
      // }, 1000);
      return;
    }
    if (favoriteLoading[symbol]) return;

    const isFav = isFavorite(symbol);
    setFavoriteLoading({ ...favoriteLoading, [symbol]: true });

    try {
      if (isFav) {
        // 取消收藏：直接传递 symbol
        await delete_symbolStarId(symbol);
        await refreshFavoriteListAfterMutation();
      } else {
        // 添加收藏：使用 GET 请求，symbol 作为 query 参数
        await post_symbolStar({
          params: {
            symbol,
          },
        });
        await refreshFavoriteListAfterMutation();
      }
    } catch (error) {
      console.error("Favorite error:", error);
    } finally {
      setFavoriteLoading({ ...favoriteLoading, [symbol]: false });
    }
  };

  // 获取币种图标（仅计算展示 URL，不修改 store，避免 MobX strict 报错）
  const getCoinIcon = (symbol) => {
    if (!symbol || typeof window === 'undefined') return require("@/assets/images/common/icon404.png").default;
    const { currencyMap } = fund;
    const coin = symbol.split("_")[0];
    const rawLogo = currencyMap?.[coin]?.["logo"];
    if (!rawLogo) return require("@/assets/images/common/icon404.png").default;
    const domain = document.domain;
    const domain2 = domain.substring(domain.indexOf(".") + 1);
    return !domain.startsWith("xt") ? rawLogo.replace("xt.com", domain2) : rawLogo;
  };

  // 格式化价格
  const formatPrice = (item) => {
    if (!item || !item.s) return "0";
    const { marketConfig } = market;
    const mConfig = marketConfig[item.s];
    const price = item.c || item.latestPrice || 0;
    if (!price || isNaN(+price)) return "0";
    return Big(price).toFixedCy(mConfig && mConfig.buyPoint ? mConfig.buyPoint : 2);
  };

  // 格式化涨跌幅
  const formatRate = (rate) => {
    return showRate(rate * 100);
  };

  // 获取涨跌幅颜色类
  const getRateClass = (rate) => {
    if (rate === null || rate === undefined || isNaN(+rate) || +rate === 0) {
      return styles["search-overlay__rate-neutral"] || "";
    }
    return +rate > 0
      ? styles["search-overlay__rate-up"] || ""
      : styles["search-overlay__rate-down"] || "";
  };

  // 仅展示 CFD 搜索结果
  const filteredSearchResults = searchResults.filter((item) => item && item.s && isCfdSymbol(item.s));

  const cfdCount = filteredSearchResults.length;

  const handleSearchBack = () => {
    setSearchValue("");
    setSearchResults([]);
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 0);
  };

  if (!visible) return null;

  const showHistoryBlock = !searchValue && searchHistory.length > 0;

  return (
    <div className={styles["search-overlay"]} onClick={onClose}>
      <div className={styles["search-overlay__content"]} onClick={(e) => e.stopPropagation()}>
        {/* 头部 */}
        <div className={styles["search-overlay__header"]}>
          {searchValue ? (
            <button
              type="button"
              className={styles["search-overlay__back"]}
              onClick={handleSearchBack}
              aria-label={t("home.cancel") || "Back"}
            >
              <img src={arrowLeftSrc} alt="" className={styles["search-overlay__arrow-left"]} aria-hidden />
            </button>
          ) : null}
          <div className={styles["search-overlay__input-wrapper"]}>
            <img
              src={searchIconSrc}
              alt=""
              className={styles["search-overlay__search-icon"]}
              aria-hidden
            />
            <input
              ref={inputRef}
              type="text"
              className={`${styles["search-overlay__input"]} ${
                searchValue ? styles["search-overlay__input--filled"] : ""
              }`}
              placeholder={t("home.enterCurrencyTradingPair") || "Enter currency trading pair"}
              value={searchValue}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {searchValue ? (
              <button
                type="button"
                className={styles["search-overlay__clear"]}
                onClick={() => {
                  setSearchValue("");
                  setSearchResults([]);
                  if (inputRef.current) {
                    inputRef.current.focus();
                  }
                }}
                aria-label={t("home.clearHistory") || "Clear"}
              >
                <img src={circledCloseSrc} alt="" className={styles["search-overlay__clear-icon"]} aria-hidden />
              </button>
            ) : null}
          </div>
          {!searchValue ? (
            <button type="button" className={styles["search-overlay__cancel"]} onClick={onClose}>
              {t("home.cancel") || "Cancel"}
            </button>
          ) : null}
        </div>

        {/* 搜索历史 */}
        {showHistoryBlock && (
          <div className={styles["search-overlay__history"]}>
            <div className={styles["search-overlay__history-header"]}>
              <div className={styles["search-overlay__history-title-row"]}>
                <span className={styles["search-overlay__history-title"]}>
                  {t("home.searchHistory") || "History"}
                </span>
                <button
                  type="button"
                  className={styles["search-overlay__history-clear"]}
                  onClick={clearAllSearchHistory}
                  title={t("home.clearHistory") || "Clear History"}
                >
                  <img src={deleteIconSrc} alt="" className={styles["search-overlay__history-clear-img"]} />
                </button>
              </div>
            </div>
            <div className={styles["search-overlay__history-list"]}>
              {searchHistory.map((item, index) => {
                // 如果未展开且超过7个，只显示前7个
                if (!historyExpanded && showExpandButton && index >= MAX_ITEMS_BEFORE_EXPAND) {
                  return null;
                }
                
                return (
                  <div
                    key={item.symbol}
                    className={styles["search-overlay__history-item"]}
                    onClick={() => handleHistoryClick(item.symbol)}
                  >
                    <span className={styles["search-overlay__history-text"]}>{item.displayName}</span>
                  </div>
                );
              })}
              {showExpandButton && (
                <button
                  className={styles["search-overlay__history-toggle-item"]}
                  onClick={() => setHistoryExpanded(!historyExpanded)}
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 16 16" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ transform: historyExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
                  >
                    <path d="M4 6L8 10L12 6" stroke="#929AA5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}

        {/* 搜索结果：仅 CFD 区块标题（样式与原先 CFD 选中 Tab 文案一致，无下划线） */}
        {searchValue && searchResults.length > 0 && (
          <div className={styles["search-overlay__cfd-results-heading"]}>
            {t("home.MarketTabCFD")}({cfdCount})
          </div>
        )}

        {/* What's New：仅区块标题；Banner 与首页 HomeQuickActions 下一致（HomeCampaignBanner）。轮播比例参考 Figma 12196:5731（345×138） */}
        {!searchValue && (
          <>
            <div className={styles["search-overlay__whats-new-section"]}>
              <div className={styles["search-overlay__whats-new-title"]}>
                {t("home.whatsNew") || "What's New"}
              </div>
              <div className={styles["search-overlay__campaign-banner-slot"]}>
                <HomeCampaignBanner />
              </div>
            </div>
            <div className={styles["search-overlay__top-searches-head"]}>
              <span className={styles["search-overlay__top-searches-title"]}>
                {t("home.topSearches") || "Top Search"}
              </span>
            </div>
          </>
        )}

        {/* 内容区域 */}
        <div
          className={`${styles["search-overlay__body"]} ${
            !searchValue ? styles["search-overlay__body--browse"] : ""
          } ${
            searchValue && filteredSearchResults.length === 0
              ? styles["search-overlay__body--search-empty"]
              : ""
          }`}
        >
          {searchValue ? (
            // 搜索结果
            <div className={styles["search-overlay__results"]}>
              {filteredSearchResults.length > 0 ? (
                filteredSearchResults.map((item, index) => (
                  <div
                    key={item.s}
                    className={styles["search-overlay__result-item"]}
                  >
                    <div 
                      className={styles["search-overlay__coin-info"]}
                      onClick={() => handleCoinClick(item.s)}
                    >
                      <img src={getCoinIcon(item.s)} alt={getCoinName(item.s)} />
                      <div className={styles["search-overlay__coin-name"]}>
                        <span className={styles["search-overlay__coin"]}>{getCoinName(item.s).toUpperCase()}</span>
                        <span className={styles["search-overlay__market"]}>
                          / {getMarketName(item.s).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className={styles["search-overlay__price-info"]}>
                      <div className={styles["search-overlay__coin-price"]}>
                        {formatPrice(item)}
                      </div>
                      <div className={`${styles["search-overlay__coin-rate"]} ${getRateClass(item.r || item.cr)}`}>
                        {formatRate(item.r || item.cr || 0)}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={styles["search-overlay__favorite"]}
                      onClick={(e) => handleFavorite(e, item.s)}
                    >
                      <img
                        src={isFavorite(item.s) ? starFilledYellow : starFilledGray}
                        alt=""
                        className={styles["search-overlay__favorite-img"]}
                      />
                    </button>
                  </div>
                ))
              ) : (
                <div className={styles["search-overlay__empty-state"]}>
                  <img
                    src={notFoundDataSrc}
                    alt=""
                    className={styles["search-overlay__empty-illustration"]}
                    width={96}
                    height={96}
                  />
                  <p className={styles["search-overlay__empty-label"]}>
                    {t("home.noDataFound") || "No data found"}
                  </p>
                </div>
              )}
            </div>
          ) : (
            // Tab 内容
            <div className={styles["search-overlay__list"]}>
              {topSearches.length > 0 ? (
                topSearches.map((item, index) => (
                  <div
                    key={item.s}
                    className={styles["search-overlay__list-item"]}
                  >
                    <div className={styles["search-overlay__item-index"]}>
                      <span
                        className={`${styles["search-overlay__index-number"]} ${
                          index < 3
                            ? styles["search-overlay__index-number--top"]
                            : styles["search-overlay__index-number--rest"]
                        }`}
                      >
                        {index + 1}
                      </span>
                    </div>
                    <div 
                      className={styles["search-overlay__coin-info"]}
                      onClick={() => handleCoinClick(item.s, item.marketType)}
                    >
                      <img src={getCoinIcon(item.s)} alt={getCoinName(item.s)} />
                      <div className={styles["search-overlay__coin-name"]}>
                        <span className={styles["search-overlay__coin"]}>{getCoinName(item.s).toUpperCase()}</span>
                        <span className={styles["search-overlay__market"]}>
                          / {getMarketName(item.s).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className={styles["search-overlay__price-info"]}>
                      <div className={styles["search-overlay__coin-price"]}>
                        {formatPrice(item)}
                      </div>
                      <div className={`${styles["search-overlay__coin-rate"]} ${getRateClass(item.r || item.cr)}`}>
                        {formatRate(item.r || item.cr || 0)}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={styles["search-overlay__favorite"]}
                      onClick={(e) => handleFavorite(e, item.s)}
                    >
                      <img
                        src={isFavorite(item.s) ? starFilledYellow : starFilledGray}
                        alt=""
                        className={styles["search-overlay__favorite-img"]}
                      />
                    </button>
                  </div>
                ))
              ) : (
                <div className={styles["search-overlay__empty"]}>
                  {t("home.noData") || "No data"}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default observer(HomeSearchOverlay);
